<template>
	<div class="tour-schedule-calendar">
		<div class="tour-schedule-calendar__header">
			<div class="tour-schedule-calendar__header-actions">
				<div class="tour-schedule-calendar__time-config">
					<label>Start:</label>
					<input type="time" v-model="startTime" class="tour-schedule-calendar__time-input" />
					<label>End:</label>
					<input type="time" v-model="endTime" class="tour-schedule-calendar__time-input" />
				</div>

				<!-- Drive Time Indicator -->
				<div class="tour-schedule-calendar__drive-time-indicator" v-if="totalDriveTime > 0">
					<span class="tour-schedule-calendar__drive-time-label">Drive Time:</span>
					<span
						class="tour-schedule-calendar__drive-time-value"
						:class="{
							'tour-schedule-calendar__drive-time-value--warning': driveTimePercentage >= 80,
							'tour-schedule-calendar__drive-time-value--danger': driveTimePercentage >= 100
						}"
					>
						{{ Math.floor(totalDriveTime / 60) }}h {{ totalDriveTime % 60 }}m / {{ Math.floor(maxDriveTimePerDay / 60) }}h {{ maxDriveTimePerDay % 60 }}m
					</span>
					<span
						class="tour-schedule-calendar__drive-time-percentage"
						:class="{
							'tour-schedule-calendar__drive-time-percentage--warning': driveTimePercentage >= 80,
							'tour-schedule-calendar__drive-time-percentage--danger': driveTimePercentage >= 100
						}"
					>
						({{ driveTimePercentage }}%)
					</span>
				</div>

				<themed-button variant="default" size="sm" @click="openAddEventModal">
					<theme-icon icon="plus" type="icons" />
					Add Comp Visit
				</themed-button>
			</div>
		</div>

		<div class="tour-schedule-calendar__content">
			<!-- Calendar -->
			<div class="tour-schedule-calendar__calendar">
				<div class="tour-schedule-calendar__grid">
					<div class="tour-schedule-calendar__time-column">
						<div v-for="hour in hours" :key="hour" class="tour-schedule-calendar__time-label">
							{{ formatHour(hour) }}
						</div>
					</div>

					<div class="tour-schedule-calendar__events-area" @click="handleGridClick">
						<div v-for="hour in hours" :key="`grid-${hour}`" class="tour-schedule-calendar__hour-line" :data-hour="hour"></div>

						<div
							v-for="(event, index) in events"
							:key="event.id"
							class="tour-schedule-calendar__event"
							:class="[
								`tour-schedule-calendar__event--${event.type}`,
								{ 'tour-schedule-calendar__event--dragging': draggingIndex === index },
								{ 'tour-schedule-calendar__event--error': event.isError }
							]"
							:style="getEventStyle(event)"
							@click="editEvent(index)"
							@mousedown="startDrag($event, index)"
						>
							<div class="tour-schedule-calendar__event-drag" @click.stop>
								<theme-icon icon="drag-handle" type="icons" />
							</div>
							<div class="tour-schedule-calendar__event-content">
								<div class="tour-schedule-calendar__event-info">
									<span class="tour-schedule-calendar__event-time">{{ formatEventTime(event.startTime) }} ({{ event.duration }}min)</span>
									<span class="tour-schedule-calendar__event-separator">•</span>
									<theme-icon :icon="getEventIcon(event.type)" type="icons" />
									<span class="tour-schedule-calendar__event-title">{{ getEventTitle(event) }}</span>
								</div>
								<div v-if="event.isError" class="tour-schedule-calendar__event-error-message">
									⚠️ {{ event.errorMessage }}
								</div>
							</div>
							<button
								v-if="!(event.isDefault && event.type === 'lunch') && !(event.isAuto && event.type === 'drive')"
								class="tour-schedule-calendar__event-remove"
								@click.stop="removeEvent(index)"
							>
								<theme-icon icon="close" type="icons" />
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Map -->
			<div class="tour-schedule-calendar__map">
				<!-- Geofence Editing Controls - Overlay at top right -->
				<div v-if="editingGeofenceCompId" class="tour-schedule-calendar__geofence-overlay">
					<span class="tour-schedule-calendar__geofence-overlay-text">
						<theme-icon icon="location" type="icons" />
						Editing Geofence (Auto-saves)
					</span>
					<themed-button variant="default" size="sm" @click="cancelGeofenceEdit">
						Done
					</themed-button>
				</div>

				<div v-if="!hasComps" class="tour-schedule-calendar__map-empty">
					<theme-icon icon="location" type="icons" />
					<p>Add comps to see them on the map</p>
				</div>
				<div v-else ref="mapContainer" class="tour-schedule-calendar__map-container"></div>
			</div>
		</div>

		<!-- Add/Edit Modal -->
		<themed-modal
			:name="`tour-schedule-event-modal-${tour.id}`"
			:title="getModalTitle()"
			size="md"
			:show-close="true"
			@closed="closeModal"
		>
			<div class="tour-schedule-calendar__modal-body">
				<!-- Type field - only shown when editing lunch break -->
				<div v-if="eventForm.type === 'lunch'" class="tour-schedule-calendar__modal-field">
					<label>Type:</label>
					<themed-select
						v-model="eventForm.type"
						:options="[{ value: 'lunch', label: 'Lunch Break' }]"
						:disabled="true"
					/>
					<small style="color: #6b7280; display: block; margin-top: 4px;">
						Lunch break type cannot be changed
					</small>
				</div>

				<!-- Comp selector - only for comp visits -->
				<div v-if="eventForm.type === 'comp'" class="tour-schedule-calendar__modal-field">
					<label>Comp:</label>
					<themed-select
						v-model="eventForm.compId"
						:options="compOptions"
						placeholder="-- Select a comp --"
					/>
				</div>

				<div class="tour-schedule-calendar__modal-field">
					<label>Start Time:</label>
					<themed-input
						type="time"
						v-model="eventForm.startTime"
					/>
				</div>

				<div class="tour-schedule-calendar__modal-field">
					<label>Duration:</label>
					<themed-select
						v-model.number="eventForm.duration"
						:options="durationOptions"
					/>
				</div>
			</div>

			<template #footer>
				<div class="tour-schedule-calendar__modal-actions">
					<themed-button variant="secondary" @click="closeModalAndHide">Cancel</themed-button>
					<themed-button variant="default" @click="saveEvent">{{ showEditEventModal ? 'Update' : 'Add' }}</themed-button>
				</div>
			</template>
		</themed-modal>
	</div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";
import ThemeIcon from '../ThemeIcon.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedModal from '../ThemedModal.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedSelect from '../ThemedSelect.vue';

export default {
	name: 'TourScheduleCalendar',
	components: { ThemeIcon, ThemedButton, ThemedModal, ThemedInput, ThemedSelect },
	props: {
		tour: { type: Object, required: true },
		availableComps: { type: Array, default: () => [] }
	},
	data() {
		return {
			events: [],
			startTime: '07:00',
			endTime: '18:00',
			showAddEventModal: false,
			showEditEventModal: false,
			editingIndex: null,
			eventForm: {
				type: 'comp',
				compId: null,
				startTime: '09:00',
				duration: 60
			},
			map: null,
			markers: [],
			polyline: null,
			directionsService: null,
			directionsRenderer: null,
			draggingIndex: null,
			dragStartY: 0,
			dragStartTime: null,
			isDragging: false,
			googleMapsLoaded: false,
			isDarkMode: false,
			themeObserver: null,
			maxDriveTimePerDay: 180, // Maximum 3 hours (180 minutes) of driving per day
			// Geofence editing
			drawingManager: null,
			geofenceCircles: {}, // Store circles by comp ID
			editingGeofenceCompId: null,
			currentEditingCircle: null,
			editingBuildingLocation: null, // Store building location for validation
			defaultGeofenceRadius: 100, // Default radius in meters
			saveGeofenceTimeout: null, // Debounce timer for saving
			isSavingGeofence: false // Flag to prevent recursive saves
		};
	},
	computed: {
		hours() {
			const [startHour] = this.startTime.split(':').map(Number);
			const [endHour] = this.endTime.split(':').map(Number);
			const hours = [];
			for (let h = startHour; h <= endHour; h++) hours.push(h);
			return hours;
		},
		startHour() {
			return parseInt(this.startTime.split(':')[0]);
		},
		hasComps() {
			return this.events.some(e => e.type === 'comp');
		},
		compEvents() {
			return this.events.filter(e => e.type === 'comp');
		},
		totalDriveTime() {
			// Calculate total drive time in minutes
			return this.events
				.filter(e => e.type === 'drive')
				.reduce((total, event) => total + event.duration, 0);
		},
		remainingDriveTime() {
			// Calculate remaining available drive time
			return this.maxDriveTimePerDay - this.totalDriveTime;
		},
		driveTimePercentage() {
			// Calculate percentage of max drive time used
			return Math.round((this.totalDriveTime / this.maxDriveTimePerDay) * 100);
		},
		eventTypeOptions() {
			// Only allow adding comp visits
			// Lunch is added by default, drive times are auto-calculated
			return [
				{ value: 'comp', label: 'Comp Visit' }
			];
		},
		compOptions() {
			return this.availableComps.map(comp => ({
				value: comp.pivot?.id || comp.id,
				label: this.getCompName(comp)
			}));
		},
		durationOptions() {
			if (this.eventForm.type === 'drive') {
				return [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map(min => ({
					value: min,
					label: `${min} min`
				}));
			}
			return [
				{ value: 30, label: '30 min' },
				{ value: 45, label: '45 min' },
				{ value: 60, label: '60 min' }
			];
		}
	},
	watch: {
		compEvents: {
			handler() {
				this.$nextTick(() => this.updateMap());
			},
			deep: true
		},
		availableComps: {
			handler(newVal) {
				if (newVal && newVal.length > 0 && this.events.length === 0) {
					this.loadExistingComps();
				}
			},
			immediate: true
		},
		tour: {
			handler(newVal) {
				console.log('Tour prop changed:', newVal);
				if (newVal) {
					this.loadExistingComps();
				}
			},
			deep: true,
			immediate: true
		}
	},
	mounted() {
		this.detectDarkMode();
		this.setupThemeObserver();
		this.initGoogleMaps();
		this.loadExistingComps();
		this.ensureDefaultLunchBreak();

		// Recalculate drive times after loading to ensure they're up to date
		this.$nextTick(() => {
			this.recalculateDriveTimes();
		});
	},
	beforeDestroy() {
		document.removeEventListener('mousemove', this.onDrag);
		document.removeEventListener('mouseup', this.stopDrag);

		if (this.themeObserver) {
			this.themeObserver.disconnect();
		}
	},
	methods: {
		formatEventTime(time24) {
			// Convert 24-hour time (HH:mm) to 12-hour format with AM/PM
			const [hours, minutes] = time24.split(':').map(Number);
			const period = hours >= 12 ? 'PM' : 'AM';
			const hours12 = hours % 12 || 12;
			return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
		},
		getThemeColors() {
			// NOTE: Google Maps InfoWindows require inline HTML with inline styles.
			// We cannot use CSS variables or LESS variables in InfoWindow content.
			// These colors are matched to our theme's color palette defined in variables.less
			//
			// Light mode colors match: --background, --foreground, --muted-foreground, --border
			// Dark mode colors match: gray-800, gray-100, gray-300, gray-400, gray-700
			//
			// If theme colors change, update these values to match.

			if (this.isDarkMode) {
				return {
					bgColor: '#1f2937',        // Matches dark theme card background
					textColor: '#f3f4f6',      // Matches dark theme text
					secondaryTextColor: '#d1d5db', // Matches dark theme secondary text
					mutedTextColor: '#9ca3af',     // Matches dark theme muted text
					borderColor: '#374151'     // Matches dark theme border
				};
			} else {
				return {
					bgColor: '#ffffff',        // Matches light theme card background
					textColor: '#111827',      // Matches light theme text
					secondaryTextColor: '#374151', // Matches light theme secondary text
					mutedTextColor: '#4b5563',     // Matches light theme muted text
					borderColor: '#d1d5db'     // Matches light theme border
				};
			}
		},
		detectDarkMode() {
			const htmlElement = document.documentElement;
			const bodyElement = document.body;
			this.isDarkMode = htmlElement.classList.contains('dark') ||
			                  bodyElement.classList.contains('dark') ||
			                  htmlElement.getAttribute('data-theme') === 'dark';
		},
		setupThemeObserver() {
			// Watch for theme changes and reinitialize map
			this.themeObserver = new MutationObserver(() => {
				const oldDarkMode = this.isDarkMode;
				this.detectDarkMode();

				if (oldDarkMode !== this.isDarkMode) {
					// Reinitialize map with new theme
					if (this.map && this.googleMapsLoaded) {
						this.reinitializeMapWithTheme();
					}
				}
			});

			this.themeObserver.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ['class', 'data-theme']
			});

			this.themeObserver.observe(document.body, {
				attributes: true,
				attributeFilter: ['class', 'data-theme']
			});
		},
		reinitializeMapWithTheme() {
			// Save current map state
			const currentCenter = this.map.getCenter();
			const currentZoom = this.map.getZoom();

			// Clear existing map
			this.clearMapMarkers();

			// Reinitialize with new theme
			this.initializeMap();

			// Restore map state
			if (currentCenter) {
				this.map.setCenter(currentCenter);
				this.map.setZoom(currentZoom);
			}
		},
		loadExistingComps() {
			// Load existing tour comps as events
			// Handle both camelCase (tourComps) and snake_case (tour_comps) from backend
			const tourComps = this.tour.tourComps || this.tour.tour_comps;

			if (tourComps && tourComps.length > 0) {
				this.events = tourComps.map((tourComp, index) => {
					// tourComp might have project_comp nested or be the comp itself
					const compId = tourComp.comp_id || tourComp.project_comp_id || tourComp.pivot?.id || tourComp.id;

					// Determine slot type - default to 'comp' if not specified
					const slotType = tourComp.slot_type || 'comp';

					// Extract time from start_date_time if available
					let startTime = `${8 + index}:30`;
					if (tourComp.start_date_time) {
						// Parse the datetime string directly to avoid timezone issues
						// Format: "2026-02-07 10:45:00" or "2026-02-07T10:45:00"
						const dateTimeStr = String(tourComp.start_date_time).replace('Z', '').replace('T', ' ');
						const timePart = dateTimeStr.split(' ')[1]; // Get the time part

						if (timePart) {
							startTime = timePart.substring(0, 5); // Get HH:MM
						}
					} else if (tourComp.scheduled_time) {
						// If scheduled_time is already in H:i format, use it
						// If it's a full datetime, extract the time
						const scheduledTimeStr = String(tourComp.scheduled_time);
						if (scheduledTimeStr.includes(' ')) {
							const timePart = scheduledTimeStr.split(' ')[1];
							startTime = timePart.substring(0, 5); // Get HH:MM
						} else {
							startTime = scheduledTimeStr;
						}
					}

					// Ensure startTime is in HH:MM format
					if (startTime && !startTime.match(/^\d{2}:\d{2}$/)) {
						const parts = startTime.split(':');
						if (parts.length >= 2) {
							const hours = parseInt(parts[0]).toString().padStart(2, '0');
							const minutes = parts[1].substring(0, 2).padStart(2, '0');
							startTime = `${hours}:${minutes}`;
						}
					}

					const event = {
						id: tourComp.id || Date.now() + index,
						type: slotType,
						compId: compId,
						startTime: startTime,
						duration: tourComp.duration_minutes || tourComp.duration || 60,
						isDefault: slotType === 'lunch' && tourComp.is_default, // Mark default lunch breaks
						isAuto: slotType === 'drive' && (tourComp.is_auto !== false) // Mark auto-generated drive times (default to true for drive times unless explicitly false)
					};

					return event;
				});
			} else {
				this.events = [];
			}
		},
		ensureDefaultLunchBreak() {
			// Check if there's already a lunch break
			const hasLunchBreak = this.events.some(event => event.type === 'lunch');

			if (!hasLunchBreak) {
				// Add default lunch break at 1:00 PM
				const defaultLunch = {
					id: 'default-lunch-' + Date.now(),
					type: 'lunch',
					compId: null,
					startTime: '13:00', // 1:00 PM
					duration: 60,
					brokerName: '',
					brokerEmail: '',
					brokerPhone: '',
					isDefault: true // Mark as default so it can't be deleted
				};

				this.events.push(defaultLunch);

				// Auto-save the schedule with the default lunch break
				this.$nextTick(() => {
					this.saveScheduleToBackend();
				});
			}
		},
		async recalculateDriveTimes() {
			// Remove all existing auto-generated drive times
			// Keep only manual drive times that user explicitly added
			this.events = this.events.filter(event => {
				// Keep all non-drive events
				if (event.type !== 'drive') return true;
				// Keep only manual drive times (not auto-generated)
				return !event.isAuto;
			});

			// Get all comp events sorted by start time
			const compEvents = this.events
				.filter(e => e.type === 'comp')
				.sort((a, b) => {
					const timeA = a.startTime.split(':').map(Number);
					const timeB = b.startTime.split(':').map(Number);
					const minutesA = timeA[0] * 60 + timeA[1];
					const minutesB = timeB[0] * 60 + timeB[1];
					return minutesA - minutesB;
				});

			// If less than 2 comps, no drive time needed
			if (compEvents.length < 2) {
				return;
			}

			// Get lunch breaks to check for conflicts
			const lunchBreaks = this.events.filter(e => e.type === 'lunch');

			// Calculate drive time between consecutive comps
			for (let i = 0; i < compEvents.length - 1; i++) {
				const currentComp = compEvents[i];
				const nextComp = compEvents[i + 1];

				// Get comp details for distance calculation
				const currentCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === currentComp.compId);
				const nextCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === nextComp.compId);

				if (!currentCompData || !nextCompData) continue;

				const currentLat = currentCompData.latitude || currentCompData.building?.latitude;
				const currentLng = currentCompData.longitude || currentCompData.building?.longitude;
				const nextLat = nextCompData.latitude || nextCompData.building?.latitude;
				const nextLng = nextCompData.longitude || nextCompData.building?.longitude;

				if (!currentLat || !currentLng || !nextLat || !nextLng) continue;

				// Calculate distance and estimated drive time
				const distance = this.calculateDistance(currentLat, currentLng, nextLat, nextLng);
				// Estimate: 30 mph average speed in city = 0.5 miles per minute
				// Add 5 minutes buffer for parking, etc.
				const driveMinutes = Math.max(10, Math.round((distance / 0.5) + 5));
				// Round to nearest 5 minutes
				const roundedDriveMinutes = Math.round(driveMinutes / 5) * 5;

				// Calculate start time: right after current comp ends
				const [currentHours, currentMinutes] = currentComp.startTime.split(':').map(Number);
				const currentEndMinutes = currentHours * 60 + currentMinutes + currentComp.duration;

				// Check if lunch break falls between current comp end and next comp start
				let driveStartMinutes = currentEndMinutes;

				// Get next comp start time for lunch check
				const [nextHours, nextMinutes] = nextComp.startTime.split(':').map(Number);
				const nextCompStartMinutes = nextHours * 60 + nextMinutes;

				for (const lunch of lunchBreaks) {
					const [lunchHours, lunchMinutes] = lunch.startTime.split(':').map(Number);
					const lunchStartMinutes = lunchHours * 60 + lunchMinutes;
					const lunchEndMinutes = lunchStartMinutes + lunch.duration;

					// Only adjust if lunch actually falls between current comp end and next comp start
					if (lunchStartMinutes >= currentEndMinutes && lunchEndMinutes <= nextCompStartMinutes) {
						// Drive should start after lunch ends
						driveStartMinutes = Math.max(driveStartMinutes, lunchEndMinutes);
					}
				}

				const driveStartHours = Math.floor(driveStartMinutes / 60);
				const driveStartMinutesRemainder = driveStartMinutes % 60;

				// Verify the drive time doesn't overlap with next comp

				// If drive would end after next comp starts, create an ERROR drive time slot
				if (driveStartMinutes + roundedDriveMinutes > nextCompStartMinutes) {
					const availableMinutes = nextCompStartMinutes - driveStartMinutes;

					// Create an ERROR drive time event that shows the conflict
					const errorDriveEvent = {
						id: `error-drive-${i}-${Date.now()}`,
						type: 'drive',
						compId: null,
						startTime: `${driveStartHours.toString().padStart(2, '0')}:${driveStartMinutesRemainder.toString().padStart(2, '0')}`,
						duration: availableMinutes > 0 ? availableMinutes : 10, // Show at least 10 min slot
						brokerName: '',
						brokerEmail: '',
						brokerPhone: '',
						isAuto: true,
						isDefault: false,
						isError: true, // Mark as error
						errorMessage: `Insufficient time! Need ${roundedDriveMinutes} min, only ${availableMinutes} min available`,
						fromCompId: currentComp.compId,
						toCompId: nextComp.compId,
						requiredDuration: roundedDriveMinutes
					};

					this.events.push(errorDriveEvent);
					continue;
				}

				// Create auto-generated drive time event
				const driveEvent = {
					id: `auto-drive-${i}-${Date.now()}`,
					type: 'drive',
					compId: null,
					startTime: `${driveStartHours.toString().padStart(2, '0')}:${driveStartMinutesRemainder.toString().padStart(2, '0')}`,
					duration: roundedDriveMinutes,
					brokerName: '',
					brokerEmail: '',
					brokerPhone: '',
					isAuto: true, // Mark as auto-generated
					isDefault: false,
					isError: false,
					fromCompId: currentComp.compId,
					toCompId: nextComp.compId
				};

				this.events.push(driveEvent);
			}
		},
		calculateEstimatedDriveTime(newCompId) {
			// Calculate what the total drive time would be if we add this comp
			const newCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === newCompId);
			if (!newCompData) return 0;

			const newLat = newCompData.latitude || newCompData.building?.latitude;
			const newLng = newCompData.longitude || newCompData.building?.longitude;
			if (!newLat || !newLng) return 0;

			// Get existing comp events sorted by time
			const existingComps = this.events
				.filter(e => e.type === 'comp')
				.sort((a, b) => {
					const timeA = a.startTime.split(':').map(Number);
					const timeB = b.startTime.split(':').map(Number);
					const minutesA = timeA[0] * 60 + timeA[1];
					const minutesB = timeB[0] * 60 + timeB[1];
					return minutesA - minutesB;
				});

			if (existingComps.length === 0) {
				// First comp, no drive time needed
				return 0;
			}

			// Calculate drive time from last comp to new comp
			const lastComp = existingComps[existingComps.length - 1];
			const lastCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === lastComp.compId);

			if (!lastCompData) return 0;

			const lastLat = lastCompData.latitude || lastCompData.building?.latitude;
			const lastLng = lastCompData.longitude || lastCompData.building?.longitude;

			if (!lastLat || !lastLng) return 0;

			// Calculate distance and estimated drive time
			const distance = this.calculateDistance(lastLat, lastLng, newLat, newLng);
			const driveMinutes = Math.max(10, Math.round((distance / 0.5) + 5));
			const roundedDriveMinutes = Math.round(driveMinutes / 5) * 5;

			return roundedDriveMinutes;
		},
		checkOverlapWithRecalculatedDriveTimes(draggedCompIndex) {
			// Simulate what the schedule would look like with recalculated drive times
			// Get all comp events sorted by start time
			const compEvents = this.events
				.filter(e => e.type === 'comp')
				.sort((a, b) => {
					const timeA = a.startTime.split(':').map(Number);
					const timeB = b.startTime.split(':').map(Number);
					const minutesA = timeA[0] * 60 + timeA[1];
					const minutesB = timeB[0] * 60 + timeB[1];
					return minutesA - minutesB;
				});

			if (compEvents.length < 2) return false;

			// Build a simulated schedule with comps and their drive times
			const simulatedEvents = [];

			for (let i = 0; i < compEvents.length; i++) {
				const currentComp = compEvents[i];
				simulatedEvents.push({
					...currentComp,
					isComp: true
				});

				// Add drive time to next comp if there is one
				if (i < compEvents.length - 1) {
					const nextComp = compEvents[i + 1];

					// Calculate drive time between these comps
					const currentCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === currentComp.compId);
					const nextCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === nextComp.compId);

					if (currentCompData && nextCompData) {
						const currentLat = currentCompData.latitude || currentCompData.building?.latitude;
						const currentLng = currentCompData.longitude || currentCompData.building?.longitude;
						const nextLat = nextCompData.latitude || nextCompData.building?.latitude;
						const nextLng = nextCompData.longitude || nextCompData.building?.longitude;

						if (currentLat && currentLng && nextLat && nextLng) {
							const distance = this.calculateDistance(currentLat, currentLng, nextLat, nextLng);
							const driveMinutes = Math.max(10, Math.round((distance / 0.5) + 5));
							const roundedDriveMinutes = Math.round(driveMinutes / 5) * 5;

							// Calculate drive time start: right after current comp ends
							const [currentHours, currentMinutes] = currentComp.startTime.split(':').map(Number);
							const currentEndMinutes = currentHours * 60 + currentMinutes + currentComp.duration;
							const driveStartHours = Math.floor(currentEndMinutes / 60);
							const driveStartMinutes = currentEndMinutes % 60;

							simulatedEvents.push({
								type: 'drive',
								startTime: `${driveStartHours.toString().padStart(2, '0')}:${driveStartMinutes.toString().padStart(2, '0')}`,
								duration: roundedDriveMinutes,
								isDriveTime: true
							});
						}
					}
				}
			}

			// Now check if any simulated events overlap with non-comp events (lunch, manual drive times)
			const nonCompEvents = this.events.filter(e => e.type !== 'comp' && !(e.type === 'drive' && e.isAuto));

			for (const simEvent of simulatedEvents) {
				const [simHours, simMinutes] = simEvent.startTime.split(':').map(Number);
				const simStartMinutes = simHours * 60 + simMinutes;
				const simEndMinutes = simStartMinutes + simEvent.duration;

				for (const nonCompEvent of nonCompEvents) {
					const [nonCompHours, nonCompMinutes] = nonCompEvent.startTime.split(':').map(Number);
					const nonCompStartMinutes = nonCompHours * 60 + nonCompMinutes;
					const nonCompEndMinutes = nonCompStartMinutes + nonCompEvent.duration;

					// Check for overlap
					if (simStartMinutes < nonCompEndMinutes && simEndMinutes > nonCompStartMinutes) {
						return true; // Overlap detected
					}
				}
			}

			// Also check if comps overlap with each other
			for (let i = 0; i < simulatedEvents.length; i++) {
				for (let j = i + 1; j < simulatedEvents.length; j++) {
					const event1 = simulatedEvents[i];
					const event2 = simulatedEvents[j];

					const [hours1, minutes1] = event1.startTime.split(':').map(Number);
					const start1 = hours1 * 60 + minutes1;
					const end1 = start1 + event1.duration;

					const [hours2, minutes2] = event2.startTime.split(':').map(Number);
					const start2 = hours2 * 60 + minutes2;
					const end2 = start2 + event2.duration;

					if (start1 < end2 && end1 > start2) {
						return true; // Overlap detected
					}
				}
			}

			return false; // No overlaps
		},
		initGoogleMaps() {
			const loader = new Loader({
				apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
				version: "quarterly",
				libraries: ["places", "drawing", "geometry"]
			});
			loader.load().then(() => {
				this.googleMapsLoaded = true;
				if (this.hasComps) this.initializeMap();
			}).catch(error => console.error('Google Maps error:', error));
		},
		openAddEventModal() {
			this.showAddEventModal = true;
			this.showEditEventModal = false;
			this.$modal.show(`tour-schedule-event-modal-${this.tour.id}`);
		},
		handleGridClick(e) {
			if (e.target.closest('.tour-schedule-calendar__event')) return;
			const rect = e.currentTarget.getBoundingClientRect();
			const y = e.clientY - rect.top;
			const minutesFromStart = (y / 60) * 60;
			const hours = Math.floor(minutesFromStart / 60) + this.startHour;
			const minutes = Math.round((minutesFromStart % 60) / 15) * 15;
			this.eventForm.startTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
			this.showAddEventModal = true;
			this.$modal.show(`tour-schedule-event-modal-${this.tour.id}`);
		},
		startDrag(e, index) {
			if (!e.target.closest('.tour-schedule-calendar__event-drag')) return;

			const event = this.events[index];

			// Silently prevent dragging of auto-generated drive times (do nothing)
			if (event.type === 'drive' && event.isAuto) {
				return;
			}

			e.preventDefault();
			e.stopPropagation(); // Prevent click event from firing
			this.draggingIndex = index;
			this.dragStartY = e.clientY;
			this.dragStartTime = this.events[index].startTime;
			this.isDragging = false; // Track if actual dragging occurred
			document.addEventListener('mousemove', this.onDrag);
			document.addEventListener('mouseup', this.stopDrag);
		},
		onDrag(e) {
			if (this.draggingIndex === null) return;
			const deltaY = e.clientY - this.dragStartY;

			// Mark that actual dragging has occurred (moved more than 5px)
			if (Math.abs(deltaY) > 5) {
				this.isDragging = true;
			}

			const event = this.events[this.draggingIndex];
			const [startHours, startMinutes] = this.dragStartTime.split(':').map(Number);
			const startTotalMinutes = startHours * 60 + startMinutes;
			const deltaMinutes = Math.round(deltaY);
			const newTotalMinutes = startTotalMinutes + deltaMinutes;
			const roundedMinutes = Math.round(newTotalMinutes / 15) * 15;
			const newHours = Math.floor(roundedMinutes / 60);
			const newMinutes = roundedMinutes % 60;
			const minHour = this.startHour;
			const maxHour = parseInt(this.endTime.split(':')[0]);

			if (newHours >= minHour && newHours <= maxHour) {
				const proposedTime = `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;

				// For comp events, we need to check if the new position would cause overlaps
				// after recalculating drive times
				if (event.type === 'comp') {
					// Temporarily update the time to check for conflicts
					const originalTime = event.startTime;
					event.startTime = proposedTime;

					// Check if this would cause overlaps with recalculated drive times
					const wouldCauseOverlap = this.checkOverlapWithRecalculatedDriveTimes(this.draggingIndex);

					if (wouldCauseOverlap) {
						// Revert to original time
						event.startTime = originalTime;
					}
				} else {
					// For non-comp events (lunch, manual drive time), check normal overlap
					const proposedStartMinutes = newHours * 60 + newMinutes;
					const proposedEndMinutes = proposedStartMinutes + event.duration;

					const wouldOverlap = this.events.some((otherEvent, index) => {
						if (index === this.draggingIndex) return false;

						const [otherHours, otherMinutes] = otherEvent.startTime.split(':').map(Number);
						const otherStartMinutes = otherHours * 60 + otherMinutes;
						const otherEndMinutes = otherStartMinutes + otherEvent.duration;

						return (proposedStartMinutes < otherEndMinutes && proposedEndMinutes > otherStartMinutes);
					});

					// Only update if no overlap
					if (!wouldOverlap) {
						event.startTime = proposedTime;
					}
				}
			}
		},
		stopDrag() {
			const wasDragging = this.isDragging;
			const draggedEvent = wasDragging ? this.events[this.draggingIndex] : null;

			this.draggingIndex = null;
			this.dragStartY = 0;
			this.dragStartTime = null;
			this.isDragging = false;
			document.removeEventListener('mousemove', this.onDrag);
			document.removeEventListener('mouseup', this.stopDrag);

			// Only auto-save if actual dragging occurred
			if (wasDragging) {
				// Update map markers after drag to refresh numbering
				this.$nextTick(() => {
					if (this.hasComps && this.map) {
						this.updateMapMarkers();
					}

					// Recalculate drive times if a comp was dragged
					if (draggedEvent && draggedEvent.type === 'comp') {
						this.recalculateDriveTimes();
					}

					// Auto-save after drag
					this.saveScheduleToBackend();
				});
			}
		},
		formatHour(hour) {
			const ampm = hour >= 12 ? 'PM' : 'AM';
			const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
			return `${displayHour} ${ampm}`;
		},
		getEventStyle(event) {
			const [hours, minutes] = event.startTime.split(':').map(Number);
			const startMinutes = (hours - this.startHour) * 60 + minutes;
			const top = (startMinutes / 60) * 60;
			const height = (event.duration / 60) * 60;
			return { top: `${top}px`, height: `${height}px` };
		},
		getEventIcon(type) {
			return { comp: 'building', lunch: 'clock', drive: 'car' }[type] || 'calendar';
		},
		getEventTitle(event) {
			if (event.type === 'comp') {
				const comp = this.availableComps.find(c => (c.pivot?.id || c.id) === event.compId);
				return comp ? this.getCompName(comp) : 'Comp Visit';
			}
			if (event.type === 'drive') {
				if (event.isError) {
					return `Drive Time - ERROR`;
				}
				if (event.isAuto) {
					return 'Drive Time (Auto)';
				}
				return 'Drive Time';
			}
			return { lunch: 'Lunch Break' }[event.type] || 'Event';
		},
		getModalTitle() {
			if (this.showEditEventModal) {
				if (this.eventForm.type === 'lunch') {
					return 'Edit Lunch Break';
				}
				return 'Edit Comp Visit';
			}
			return 'Add Comp Visit';
		},
		editEvent(index) {
			const event = this.events[index];

			// Silently ignore clicks on auto-generated drive times (do nothing)
			if (event.type === 'drive' && event.isAuto) {
				return;
			}

			// If it's a comp event, zoom to it on the map
			if (event.type === 'comp' && this.map) {
				const comp = this.availableComps.find(c => (c.pivot?.id || c.id) === event.compId);
				if (comp) {
					const lat = comp.latitude || comp.building?.latitude;
					const lng = comp.longitude || comp.building?.longitude;

					if (lat && lng) {
						// Zoom to the comp location
						this.map.setCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
						this.map.setZoom(17);

						// Find and open the marker's info window
						const marker = this.markers.find(m => {
							const markerPos = m.getPosition();
							return Math.abs(markerPos.lat() - parseFloat(lat)) < 0.0001 &&
							       Math.abs(markerPos.lng() - parseFloat(lng)) < 0.0001;
						});

						if (marker && marker.infoWindow) {
							// Close all other info windows
							this.markers.forEach(m => {
								if (m.infoWindow) m.infoWindow.close();
							});
							// Open this marker's info window
							marker.infoWindow.open(this.map, marker);
						}
					}
				}
			}

			this.editingIndex = index;
			this.eventForm = { ...event };
			this.showEditEventModal = true;
			this.$modal.show(`tour-schedule-event-modal-${this.tour.id}`);
		},
		removeEvent(index) {
			const event = this.events[index];

			// Prevent deletion of default lunch break
			if (event.isDefault && event.type === 'lunch') {
				this.$notify({
					type: 'warning',
					title: 'Cannot Delete',
					text: 'The default lunch break cannot be deleted. You can drag it to adjust the time.'
				});
				return;
			}

			// Prevent deletion of auto-generated drive time
			if (event.type === 'drive' && event.isAuto) {
				this.$notify({
					type: 'warning',
					title: 'Cannot Delete',
					text: 'Drive time is automatically calculated between comps and cannot be manually deleted.'
				});
				return;
			}

			const eventTitle = event.type === 'comp' ? this.getEventTitle(event) : this.getEventTitle(event);

			this.events.splice(index, 1);

			this.$notify({
				type: 'success',
				title: 'Event Removed',
				text: `${eventTitle} has been removed from the schedule`
			});

			// Recalculate drive times after removing comp
			if (event.type === 'comp') {
				this.$nextTick(() => {
					this.recalculateDriveTimes();
				});
			}

			// Auto-save the schedule
			this.saveScheduleToBackend();
		},
		async saveEvent() {
			if (this.eventForm.type === 'comp' && !this.eventForm.compId) {
				this.$notify({
					type: 'error',
					title: 'Comp Required',
					text: 'Please select a comp'
				});
				return;
			}

			// Validation 1: Check for duplicate comp
			if (this.eventForm.type === 'comp') {
				const isDuplicate = this.events.some((event, index) => {
					// Skip the current event if editing
					if (this.showEditEventModal && index === this.editingIndex) {
						return false;
					}
					return event.type === 'comp' && event.compId === this.eventForm.compId;
				});

				if (isDuplicate) {
					const comp = this.availableComps.find(c => (c.pivot?.id || c.id) === this.eventForm.compId);
					const compName = comp ? this.getCompName(comp) : 'This comp';
					this.$notify({
						type: 'error',
						title: 'Duplicate Comp',
						text: `${compName} has already been added to the schedule. Each comp can only be added once.`
					});
					return;
				}

				// Validation 2: Check if adding this comp would exceed max drive time
				if (!this.showEditEventModal) { // Only check when adding new comp, not editing
					const estimatedDriveTime = this.calculateEstimatedDriveTime(this.eventForm.compId);
					const newTotalDriveTime = this.totalDriveTime + estimatedDriveTime;

					if (newTotalDriveTime > this.maxDriveTimePerDay) {
						const hoursOver = Math.floor((newTotalDriveTime - this.maxDriveTimePerDay) / 60);
						const minutesOver = (newTotalDriveTime - this.maxDriveTimePerDay) % 60;
						const overText = hoursOver > 0
							? `${hoursOver}h ${minutesOver}m`
							: `${minutesOver} minutes`;

						this.$notify({
							type: 'error',
							title: 'Drive Time Limit Exceeded',
							text: `Adding this comp would exceed the maximum daily drive time of ${Math.floor(this.maxDriveTimePerDay / 60)}h ${this.maxDriveTimePerDay % 60}m by ${overText}. Current: ${Math.floor(this.totalDriveTime / 60)}h ${this.totalDriveTime % 60}m, Estimated addition: ${Math.floor(estimatedDriveTime / 60)}h ${estimatedDriveTime % 60}m.`
						});
						return;
					}
				}
			}

			const event = {
				id: Date.now(),
				type: this.eventForm.type,
				compId: this.eventForm.compId,
				startTime: this.eventForm.startTime,
				duration: this.eventForm.duration,
				brokerName: this.eventForm.brokerName || '',
				brokerEmail: this.eventForm.brokerEmail || '',
				brokerPhone: this.eventForm.brokerPhone || '',
				isDefault: false // Manually added events are not default
			};

			// Validation 3: Check for time overlap
			const [newHours, newMinutes] = event.startTime.split(':').map(Number);
			const newStartMinutes = newHours * 60 + newMinutes;
			const newEndMinutes = newStartMinutes + event.duration;

			const hasOverlap = this.events.some((existingEvent, index) => {
				// Skip the current event if editing
				if (this.showEditEventModal && index === this.editingIndex) {
					return false;
				}

				const [existingHours, existingMinutes] = existingEvent.startTime.split(':').map(Number);
				const existingStartMinutes = existingHours * 60 + existingMinutes;
				const existingEndMinutes = existingStartMinutes + existingEvent.duration;

				// Check if times overlap
				return (newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes);
			});

			if (hasOverlap) {
				this.$notify({
					type: 'error',
					title: 'Time Conflict',
					text: 'This event overlaps with another event. Please choose a different time or adjust the duration.'
				});
				return;
			}

			// Validation 4: For comp events, check if there's enough time for drive time
			if (event.type === 'comp') {
				const driveTimeValidation = this.validateDriveTimeFits(event);
				if (!driveTimeValidation.fits) {
					this.$notify({
						type: 'error',
						title: 'Insufficient Time for Drive',
						text: driveTimeValidation.message
					});
					return;
				}
			}

			if (this.showEditEventModal && this.editingIndex !== null) {
				this.events.splice(this.editingIndex, 1, event);
			} else {
				this.events.push(event);
			}

			this.$modal.hide(`tour-schedule-event-modal-${this.tour.id}`);
			this.closeModal();

			// Recalculate drive times and save - do this in sequence for comp events
			if (event.type === 'comp') {
				await this.$nextTick();
				await this.recalculateDriveTimes();
				await this.saveScheduleToBackend();
			} else {
				// For non-comp events, just save
				this.saveScheduleToBackend();
			}
		},
		closeModal() {
			this.showAddEventModal = false;
			this.showEditEventModal = false;
			this.editingIndex = null;
			this.eventForm = {
				type: 'comp',
				compId: null,
				startTime: '09:00',
				duration: 60,
				brokerName: '',
				brokerEmail: '',
				brokerPhone: ''
			};
		},
		closeModalAndHide() {
			this.$modal.hide(`tour-schedule-event-modal-${this.tour.id}`);
			this.closeModal();
		},
		updateMap() {
			if (!this.hasComps) {
				if (this.map) this.clearMapMarkers();
				return;
			}
			if (!this.googleMapsLoaded) return;
			if (!this.map) {
				this.initializeMap();
			} else {
				this.updateMapMarkers();
			}
		},
		initializeMap() {
			if (!this.$refs.mapContainer || !window.google) return;

			const mapStyles = this.isDarkMode ? [
				{ elementType: "geometry", stylers: [{ color: "#242f3e" }] },
				{ elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
				{ elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
				{
					featureType: "administrative.locality",
					elementType: "labels.text.fill",
					stylers: [{ color: "#d59563" }],
				},
				{
					featureType: "poi",
					elementType: "labels.text.fill",
					stylers: [{ color: "#d59563" }],
				},
				{
					featureType: "poi.park",
					elementType: "geometry",
					stylers: [{ color: "#263c3f" }],
				},
				{
					featureType: "poi.park",
					elementType: "labels.text.fill",
					stylers: [{ color: "#6b9a76" }],
				},
				{
					featureType: "road",
					elementType: "geometry",
					stylers: [{ color: "#38414e" }],
				},
				{
					featureType: "road",
					elementType: "geometry.stroke",
					stylers: [{ color: "#212a37" }],
				},
				{
					featureType: "road",
					elementType: "labels.text.fill",
					stylers: [{ color: "#9ca5b3" }],
				},
				{
					featureType: "road.highway",
					elementType: "geometry",
					stylers: [{ color: "#746855" }],
				},
				{
					featureType: "road.highway",
					elementType: "geometry.stroke",
					stylers: [{ color: "#1f2835" }],
				},
				{
					featureType: "road.highway",
					elementType: "labels.text.fill",
					stylers: [{ color: "#f3d19c" }],
				},
				{
					featureType: "transit",
					elementType: "geometry",
					stylers: [{ color: "#2f3948" }],
				},
				{
					featureType: "transit.station",
					elementType: "labels.text.fill",
					stylers: [{ color: "#d59563" }],
				},
				{
					featureType: "water",
					elementType: "geometry",
					stylers: [{ color: "#17263c" }],
				},
				{
					featureType: "water",
					elementType: "labels.text.fill",
					stylers: [{ color: "#515c6d" }],
				},
				{
					featureType: "water",
					elementType: "labels.text.stroke",
					stylers: [{ color: "#17263c" }],
				},
			] : [];

			this.map = new google.maps.Map(this.$refs.mapContainer, {
				zoom: 12,
				center: { lat: 40.7128, lng: -74.0060 },
				mapTypeControl: false,
				streetViewControl: false,
				fullscreenControl: false,
				disableDoubleClickZoom: true, // Disable double-click zoom to allow polygon drawing
				styles: mapStyles
			});

			// Initialize Directions Service and Renderer
			this.directionsService = new google.maps.DirectionsService();
			this.directionsRenderer = new google.maps.DirectionsRenderer({
				map: this.map,
				suppressMarkers: true, // We'll use custom markers
				polylineOptions: {
					strokeColor: '#4F46E5',
					strokeOpacity: 0.8,
					strokeWeight: 4
				}
			});

			// Initialize Drawing Manager for geofences (circles).
			// Google removed DrawingManager in Maps JS 3.65; the loader is pinned to the
			// quarterly channel, which still has it. Skip rather than throw if it goes.
			if (!google.maps.drawing || !google.maps.drawing.DrawingManager) {
				console.warn('Drawing library unavailable; geofence drawing disabled.');
				return;
			}

			this.drawingManager = new google.maps.drawing.DrawingManager({
				drawingMode: null, // Start with no drawing mode
				drawingControl: false, // We'll control this programmatically
				circleOptions: {
					fillColor: '#FF6B6B',
					fillOpacity: 0.3,
					strokeWeight: 2,
					strokeColor: '#FF0000',
					editable: true,
					draggable: true,
					clickable: true
				}
			});

			this.drawingManager.setMap(this.map);

			// Listen for circle complete
			google.maps.event.addListener(this.drawingManager, 'circlecomplete', (circle) => {
				this.handleCircleComplete(circle);
			});

			this.updateMapMarkers();
			this.loadExistingGeofences();
		},
		async updateMapMarkers() {
			if (!this.map) return;
			this.clearMapMarkers();
			const bounds = new google.maps.LatLngBounds();

			// Sort comp events by start time for proper route ordering
			const sortedCompEvents = [...this.compEvents].sort((a, b) => {
				const timeA = a.startTime.split(':').map(Number);
				const timeB = b.startTime.split(':').map(Number);
				const minutesA = timeA[0] * 60 + timeA[1];
				const minutesB = timeB[0] * 60 + timeB[1];
				return minutesA - minutesB;
			});

			// Build waypoints for Directions API
			const validComps = [];
			sortedCompEvents.forEach(event => {
				const comp = this.availableComps.find(c => (c.pivot?.id || c.id) === event.compId);
				if (!comp) return;

				const lat = comp.latitude || comp.building?.latitude;
				const lng = comp.longitude || comp.building?.longitude;

				if (!lat || !lng) return;
				validComps.push({ event, comp, position: { lat: parseFloat(lat), lng: parseFloat(lng) } });
			});

			if (validComps.length === 0) return;

			// If we have 2+ stops, use Directions API for real traffic data
			if (validComps.length >= 2) {
				await this.calculateRouteWithDirections(validComps, bounds);
			} else {
				// Single stop - just show marker
				this.createSingleMarker(validComps[0], 0, bounds);
			}

			if (this.markers.length > 0) this.map.fitBounds(bounds);
		},
		async calculateRouteWithDirections(validComps, bounds) {
			// Build waypoints for Directions API
			const origin = validComps[0].position;
			const destination = validComps[validComps.length - 1].position;
			const waypoints = validComps.slice(1, -1).map(comp => ({
				location: comp.position,
				stopover: true
			}));

			const request = {
				origin: origin,
				destination: destination,
				waypoints: waypoints,
				travelMode: google.maps.TravelMode.DRIVING,
				drivingOptions: {
					departureTime: new Date(), // For real-time traffic data
					trafficModel: google.maps.TrafficModel.BEST_GUESS
				},
				optimizeWaypoints: false // Keep user's order
			};

			try {
				const result = await new Promise((resolve, reject) => {
					this.directionsService.route(request, (result, status) => {
						if (status === 'OK') {
							resolve(result);
						} else {
							reject(status);
						}
					});
				});

				// Display the route
				this.directionsRenderer.setDirections(result);

				// Calculate totals from actual route
				let totalDistance = 0;
				let totalDuration = 0;
				let totalDurationInTraffic = 0;

				result.routes[0].legs.forEach(leg => {
					totalDistance += leg.distance.value; // in meters
					totalDuration += leg.duration.value; // in seconds
					totalDurationInTraffic += leg.duration_in_traffic?.value || leg.duration.value;
				});

				// Convert to miles and minutes
				const totalMiles = (totalDistance / 1609.34).toFixed(1);
				const totalMinutes = Math.round(totalDuration / 60);
				const totalMinutesInTraffic = Math.round(totalDurationInTraffic / 60);

				// Get theme-aware colors for info windows
				const colors = this.getThemeColors();

				// Create markers with real route data
				validComps.forEach((compData, index) => {
					bounds.extend(compData.position);

					const compName = this.getCompName(compData.comp);
					const address = compData.comp.address_line_1 || compData.comp.building?.address_line_1 || 'Address not available';
					const city = compData.comp.city || compData.comp.building?.city || '';
					const state = compData.comp.state || compData.comp.building?.state || '';
					const fullAddress = `${address}${city ? ', ' + city : ''}${state ? ', ' + state : ''}`;

					// Get route leg info for this segment
					let distanceInfo = '';
					if (index > 0) {
						const leg = result.routes[0].legs[index - 1];
						const prevCompName = this.getCompName(validComps[index - 1].comp);

						const trafficInfo = leg.duration_in_traffic
							? `<span style="color: ${colors.secondaryTextColor};">🚦 ${leg.duration_in_traffic.text} (with traffic)</span><br>`
							: '';

						distanceInfo = `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${colors.borderColor};">
							<strong style="color: ${colors.textColor};">From ${prevCompName}:</strong><br>
							<span style="color: ${colors.secondaryTextColor};">📍 ${leg.distance.text}</span><br>
							<span style="color: ${colors.secondaryTextColor};">🕐 ${leg.duration.text}</span><br>
							${trafficInfo}
						</div>`;
					}

					const infoContent = `
						<div style="padding: 12px; min-width: 220px; font-family: system-ui, -apple-system, sans-serif; background: ${colors.bgColor}; color: ${colors.textColor};">
							<div style="font-weight: 600; font-size: 16px; margin-bottom: 8px; color: ${colors.textColor};">
								Stop ${index + 1}: ${compName}
							</div>
							<div style="color: ${colors.secondaryTextColor}; font-size: 14px; margin-bottom: 4px;">
								🕐 ${compData.event.startTime} (${compData.event.duration} min)
							</div>
							<div style="color: ${colors.mutedTextColor}; font-size: 13px; line-height: 1.4;">
								📍 ${fullAddress}
							</div>
							${distanceInfo}
							<div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid ${colors.borderColor};">
								<button
									onclick="window.tourScheduleEditGeofence_${compData.event.id}()"
									style="width: 100%; padding: 8px 12px; background: #3b82f6; color: white; border: none; border-radius: 6px; font-weight: 500; cursor: pointer; font-size: 13px;"
									onmouseover="this.style.background='#2563eb'"
									onmouseout="this.style.background='#3b82f6'"
								>
									📍 Edit Geofence
								</button>
							</div>
						</div>
					`;

					// Create a global function for this specific comp using the tour_comp id
					window[`tourScheduleEditGeofence_${compData.event.id}`] = () => {
						this.startEditingGeofence(compData.event.id, compData);
					};

					const infoWindow = new google.maps.InfoWindow({
						content: infoContent
					});

					const marker = new google.maps.Marker({
						position: compData.position,
						map: this.map,
						label: { text: `${index + 1}`, color: 'white', fontSize: '14px', fontWeight: 'bold' },
						title: compName
					});

					marker.addListener('click', () => {
						this.markers.forEach(m => {
							if (m.infoWindow) m.infoWindow.close();
						});
						infoWindow.open(this.map, marker);
					});

					marker.infoWindow = infoWindow;
					this.markers.push(marker);
				});

				// Add route summary
				const midIndex = Math.floor(validComps.length / 2);
				const midPoint = validComps[midIndex].position;

				const trafficWarning = totalMinutesInTraffic > totalMinutes + 5
					? `<div style="color: #dc2626; font-size: 12px; margin-top: 4px;">⚠️ Heavy traffic expected</div>`
					: '';

				const routeSummary = `
					<div style="padding: 12px; font-family: system-ui, -apple-system, sans-serif; background: ${colors.bgColor}; color: ${colors.textColor};">
						<div style="font-weight: 600; font-size: 15px; margin-bottom: 8px; color: ${colors.textColor};">
							📊 Tour Route Summary
						</div>
						<div style="color: ${colors.secondaryTextColor}; font-size: 13px; line-height: 1.8;">
							🏢 ${validComps.length} stops<br>
							📍 ${totalMiles} miles total<br>
							🕐 ${totalMinutes} min driving<br>
							🚦 ${totalMinutesInTraffic} min (with traffic)<br>
							⏱️ ${validComps.reduce((sum, c) => sum + c.event.duration, 0)} min at locations
						</div>
						${trafficWarning}
					</div>
				`;

				this.routeInfoWindow = new google.maps.InfoWindow({
					content: routeSummary,
					position: midPoint
				});

			} catch (error) {
				console.error('Directions API error:', error);
				this.$notify({
					type: 'warning',
					title: 'Route Calculation',
					text: 'Could not calculate optimal route. Showing direct paths.'
				});

				// Fallback to simple markers
				validComps.forEach((compData, index) => {
					this.createSimpleMarker(compData, index, bounds, validComps);
				});
			}
		},
		createSingleMarker(compData, index, bounds) {
			bounds.extend(compData.position);

			const compName = this.getCompName(compData.comp);
			const address = compData.comp.address_line_1 || compData.comp.building?.address_line_1 || 'Address not available';
			const city = compData.comp.city || compData.comp.building?.city || '';
			const state = compData.comp.state || compData.comp.building?.state || '';
			const fullAddress = `${address}${city ? ', ' + city : ''}${state ? ', ' + state : ''}`;

			// Get theme-aware colors
			const colors = this.getThemeColors();

			const infoContent = `
				<div style="padding: 12px; min-width: 220px; font-family: system-ui, -apple-system, sans-serif; background: ${colors.bgColor}; color: ${colors.textColor};">
					<div style="font-weight: 600; font-size: 16px; margin-bottom: 8px; color: ${colors.textColor};">
						${compName}
					</div>
					<div style="color: ${colors.secondaryTextColor}; font-size: 14px; margin-bottom: 4px;">
						🕐 ${compData.event.startTime} (${compData.event.duration} min)
					</div>
					<div style="color: ${colors.mutedTextColor}; font-size: 13px; line-height: 1.4;">
						📍 ${fullAddress}
					</div>
					<div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid ${colors.borderColor};">
						<button
							onclick="window.tourScheduleEditGeofence_${compData.event.id}()"
							style="width: 100%; padding: 8px 12px; background: #3b82f6; color: white; border: none; border-radius: 6px; font-weight: 500; cursor: pointer; font-size: 13px;"
							onmouseover="this.style.background='#2563eb'"
							onmouseout="this.style.background='#3b82f6'"
						>
							📍 Edit Geofence
						</button>
					</div>
				</div>
			`;

			// Create a global function for this specific comp using the tour_comp id
			window[`tourScheduleEditGeofence_${compData.event.id}`] = () => {
				this.startEditingGeofence(compData.event.id, compData);
			};

			const infoWindow = new google.maps.InfoWindow({
				content: infoContent
			});

			const marker = new google.maps.Marker({
				position: compData.position,
				map: this.map,
				label: { text: '1', color: 'white', fontSize: '14px', fontWeight: 'bold' },
				title: compName
			});

			marker.addListener('click', () => {
				this.markers.forEach(m => {
					if (m.infoWindow) m.infoWindow.close();
				});
				infoWindow.open(this.map, marker);
			});

			marker.infoWindow = infoWindow;
			this.markers.push(marker);
		},
		createSimpleMarker(compData, index, bounds, allComps) {
			bounds.extend(compData.position);

			const compName = this.getCompName(compData.comp);
			const address = compData.comp.address_line_1 || compData.comp.building?.address_line_1 || 'Address not available';
			const city = compData.comp.city || compData.comp.building?.city || '';
			const state = compData.comp.state || compData.comp.building?.state || '';
			const fullAddress = `${address}${city ? ', ' + city : ''}${state ? ', ' + state : ''}`;

			// Get theme-aware colors
			const colors = this.getThemeColors();

			let distanceInfo = '';
			if (index > 0) {
				const prevComp = allComps[index - 1];
				const distance = this.calculateDistance(
					prevComp.position.lat, prevComp.position.lng,
					compData.position.lat, compData.position.lng
				);
				const driveTime = Math.round(distance / 60 * 60);
				const prevCompName = this.getCompName(prevComp.comp);

				distanceInfo = `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid ${colors.borderColor};">
					<strong style="color: ${colors.textColor};">From ${prevCompName}:</strong><br>
					<span style="color: ${colors.secondaryTextColor};">📍 ${distance.toFixed(1)} miles</span><br>
					<span style="color: ${colors.secondaryTextColor};">🕐 ~${driveTime} min drive</span>
				</div>`;
			}

			const infoContent = `
				<div style="padding: 12px; min-width: 220px; font-family: system-ui, -apple-system, sans-serif; background: ${colors.bgColor}; color: ${colors.textColor};">
					<div style="font-weight: 600; font-size: 16px; margin-bottom: 8px; color: ${colors.textColor};">
						Stop ${index + 1}: ${compName}
					</div>
					<div style="color: ${colors.secondaryTextColor}; font-size: 14px; margin-bottom: 4px;">
						🕐 ${compData.event.startTime} (${compData.event.duration} min)
					</div>
					<div style="color: ${colors.mutedTextColor}; font-size: 13px; line-height: 1.4;">
						📍 ${fullAddress}
					</div>
					${distanceInfo}
				</div>
			`;

			const infoWindow = new google.maps.InfoWindow({
				content: infoContent
			});

			const marker = new google.maps.Marker({
				position: compData.position,
				map: this.map,
				label: { text: `${index + 1}`, color: 'white', fontSize: '14px', fontWeight: 'bold' },
				title: compName
			});

			marker.addListener('click', () => {
				this.markers.forEach(m => {
					if (m.infoWindow) m.infoWindow.close();
				});
				infoWindow.open(this.map, marker);
			});

			marker.infoWindow = infoWindow;
			this.markers.push(marker);
		},
		calculateDistance(lat1, lon1, lat2, lon2) {
			// Haversine formula to calculate distance in miles
			const R = 3959; // Earth's radius in miles
			const dLat = (lat2 - lat1) * Math.PI / 180;
			const dLon = (lon2 - lon1) * Math.PI / 180;
			const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
					  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
					  Math.sin(dLon/2) * Math.sin(dLon/2);
			const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
			return R * c;
		},
		validateDriveTimeFits(newCompEvent) {
			// Check if adding this comp would leave enough time for drive times
			// Create a temporary events array with the new comp
			const tempEvents = [...this.events];

			// If editing, remove the old version
			if (this.showEditEventModal && this.editingIndex !== null) {
				tempEvents.splice(this.editingIndex, 1);
			}

			// Add the new comp
			tempEvents.push(newCompEvent);

			// Get all comp events sorted by time
			const compEvents = tempEvents
				.filter(e => e.type === 'comp')
				.sort((a, b) => {
					const timeA = a.startTime.split(':').map(Number);
					const timeB = b.startTime.split(':').map(Number);
					const minutesA = timeA[0] * 60 + timeA[1];
					const minutesB = timeB[0] * 60 + timeB[1];
					return minutesA - minutesB;
				});

			if (compEvents.length < 2) {
				return { fits: true };
			}

			// Get lunch breaks
			const lunchBreaks = tempEvents.filter(e => e.type === 'lunch');

			// Check each consecutive pair of comps
			for (let i = 0; i < compEvents.length - 1; i++) {
				const currentComp = compEvents[i];
				const nextComp = compEvents[i + 1];

				// Get comp data for distance calculation
				const currentCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === currentComp.compId);
				const nextCompData = this.availableComps.find(c => (c.pivot?.id || c.id) === nextComp.compId);

				if (!currentCompData || !nextCompData) continue;

				const currentLat = currentCompData.latitude || currentCompData.building?.latitude;
				const currentLng = currentCompData.longitude || currentCompData.building?.longitude;
				const nextLat = nextCompData.latitude || nextCompData.building?.latitude;
				const nextLng = nextCompData.longitude || nextCompData.building?.longitude;

				if (!currentLat || !currentLng || !nextLat || !nextLng) continue;

				// Calculate required drive time
				const distance = this.calculateDistance(currentLat, currentLng, nextLat, nextLng);
				const driveMinutes = Math.max(10, Math.round((distance / 0.5) + 5));
				const roundedDriveMinutes = Math.round(driveMinutes / 5) * 5;

				// Calculate when current comp ends
				const [currentHours, currentMinutes] = currentComp.startTime.split(':').map(Number);
				const currentEndMinutes = currentHours * 60 + currentMinutes + currentComp.duration;

				// Calculate when next comp starts
				const [nextHours, nextMinutes] = nextComp.startTime.split(':').map(Number);
				const nextCompStartMinutes = nextHours * 60 + nextMinutes;

				// Check if lunch falls between
				let driveStartMinutes = currentEndMinutes;
				for (const lunch of lunchBreaks) {
					const [lunchHours, lunchMinutes] = lunch.startTime.split(':').map(Number);
					const lunchStartMinutes = lunchHours * 60 + lunchMinutes;
					const lunchEndMinutes = lunchStartMinutes + lunch.duration;

					if (lunchStartMinutes >= currentEndMinutes && lunchEndMinutes <= nextCompStartMinutes) {
						driveStartMinutes = Math.max(driveStartMinutes, lunchEndMinutes);
					}
				}

				// Check if drive time fits
				if (driveStartMinutes + roundedDriveMinutes > nextCompStartMinutes) {
					const currentCompName = this.getCompName(currentCompData);
					const nextCompName = this.getCompName(nextCompData);
					const availableMinutes = nextCompStartMinutes - driveStartMinutes;

					return {
						fits: false,
						message: `Not enough time for ${roundedDriveMinutes} min drive between "${currentCompName}" and "${nextCompName}". Only ${availableMinutes} min available. Please adjust comp times.`
					};
				}
			}

			return { fits: true };
		},
		clearMapMarkers() {
			// Close all info windows
			this.markers.forEach(marker => {
				if (marker.infoWindow) {
					marker.infoWindow.close();
				}
				marker.setMap(null);
			});
			this.markers = [];

			if (this.polyline) {
				this.polyline.setMap(null);
				this.polyline = null;
			}

			if (this.directionsRenderer) {
				this.directionsRenderer.setMap(null);
				this.directionsRenderer.setMap(this.map);
			}

			if (this.routeInfoWindow) {
				this.routeInfoWindow.close();
				this.routeInfoWindow = null;
			}
		},
		getCompName(comp) {
			if (!comp) return '';
			return comp.building_name || comp.name || comp.address_line_1 ||
				   comp.building?.building_name || comp.building?.name || 'Unnamed';
		},
		async saveScheduleToBackend() {
			try {
				// Ensure all startTime values are in H:i format before sending
				const formattedEvents = this.events.map(event => {
					let startTime = event.startTime;

					// If startTime is null or undefined, skip this event
					if (!startTime) {
						console.error('Event has no startTime:', event);
						return null;
					}

					// Convert to string if it's not already
					startTime = String(startTime);

					// If startTime contains a space (full datetime), extract just the time
					if (startTime.includes(' ')) {
						startTime = startTime.split(' ')[1].substring(0, 5);
					}

					// Remove any AM/PM indicators if present
					startTime = startTime.replace(/\s*(AM|PM|am|pm)\s*$/i, '').trim();

					// Ensure it's in HH:MM format
					if (!startTime.match(/^\d{1,2}:\d{2}$/)) {
						console.error('Invalid time format:', startTime, 'for event:', event);
						// Try to parse and reformat
						const parts = startTime.split(':');
						if (parts.length >= 2) {
							const hours = parseInt(parts[0]).toString().padStart(2, '0');
							const minutes = parts[1].substring(0, 2).padStart(2, '0');
							startTime = `${hours}:${minutes}`;
						} else {
							// Cannot parse, return null to filter out
							return null;
						}
					} else {
						// Ensure padding for single digit hours
						const parts = startTime.split(':');
						const hours = parseInt(parts[0]).toString().padStart(2, '0');
						const minutes = parts[1].padStart(2, '0');
						startTime = `${hours}:${minutes}`;
					}

					return {
						...event,
						startTime: startTime
					};
				}).filter(event => event !== null); // Remove any null events

				// Log the formatted events for debugging
				if (formattedEvents.some(e => !e.startTime || !e.startTime.match(/^\d{2}:\d{2}$/))) {
					console.error('Invalid time format detected in events:', formattedEvents.filter(e => !e.startTime || !e.startTime.match(/^\d{2}:\d{2}$/)));
				}

				const response = await axios.put(`/tours/${this.tour.id}/schedule/update`, {
					events: formattedEvents
				});

				if (response.data.success && response.data.events) {
					// Update event IDs with the new IDs from the backend
					// This is critical because the backend deletes and recreates all tour_comps
					const newEvents = response.data.events;
					this.events = this.events.map((event, index) => {
						if (newEvents[index]) {
							return {
								...event,
								id: newEvents[index].id // Update with the new tour_comp ID
							};
						}
						return event;
					});
				}
			} catch (error) {
				console.error('Error saving schedule:', error);
				this.$notify({
					type: 'error',
					title: 'Save Failed',
					text: 'Failed to save schedule. Please try again.'
				});
			}
		},

		// Geofence Methods
		async loadExistingGeofences() {
			// Load geofences for all comps in the tour
			try {
				const response = await axios.get(`/tours/${this.tour.id}`);
				const tourComps = response.data.tour_comps || [];

				tourComps.forEach(tourComp => {
					if (tourComp.geofence_id && tourComp.geofence_polygon) {
						// Use the tour_comp id, not the comp_id
						const tourCompId = tourComp.id;
						this.displayGeofenceOnMap(tourCompId, tourComp.geofence_polygon);
					}
				});
			} catch (error) {
				console.error('Error loading geofences:', error);
			}
		},

		displayGeofenceOnMap(compId, geofenceData) {
			if (!this.map || !geofenceData) return;

			// Remove existing circle for this comp if any
			if (this.geofenceCircles[compId]) {
				this.geofenceCircles[compId].setMap(null);
			}

			// Check if geofenceData has radius (circle format)
			if (geofenceData.radius && geofenceData.center) {
				// Circle format
				const circle = new google.maps.Circle({
					center: {
						lat: parseFloat(geofenceData.center.lat),
						lng: parseFloat(geofenceData.center.lng)
					},
					radius: parseFloat(geofenceData.radius),
					fillColor: '#FF6B6B',
					fillOpacity: 0.2,
					strokeWeight: 2,
					strokeColor: '#FF0000',
					editable: false,
					draggable: false,
					map: this.map
				});

				this.geofenceCircles[compId] = circle;
			} else {
				// Legacy polygon format - convert to circle
				// Calculate center and approximate radius from polygon
				let coordinates = [];
				if (Array.isArray(geofenceData)) {
					coordinates = geofenceData.map(coord => ({
						lat: parseFloat(coord.lat),
						lng: parseFloat(coord.lng)
					}));
				} else if (geofenceData.coordinates && geofenceData.coordinates[0]) {
					// GeoJSON format
					coordinates = geofenceData.coordinates[0].map(coord => ({
						lat: coord[1],
						lng: coord[0]
					}));
				}

				if (coordinates.length >= 3) {
					// Calculate center
					const center = {
						lat: coordinates.reduce((sum, c) => sum + c.lat, 0) / coordinates.length,
						lng: coordinates.reduce((sum, c) => sum + c.lng, 0) / coordinates.length
					};

					// Calculate average distance from center as radius
					const distances = coordinates.map(coord => {
						return google.maps.geometry.spherical.computeDistanceBetween(
							new google.maps.LatLng(center.lat, center.lng),
							new google.maps.LatLng(coord.lat, coord.lng)
						);
					});
					const radius = distances.reduce((sum, d) => sum + d, 0) / distances.length;

					const circle = new google.maps.Circle({
						center: center,
						radius: radius,
						fillColor: '#FF6B6B',
						fillOpacity: 0.2,
						strokeWeight: 2,
						strokeColor: '#FF0000',
						editable: false,
						draggable: false,
						map: this.map
					});

					this.geofenceCircles[compId] = circle;
				}
			}
		},

		startEditingGeofence(compId, compData) {
			// Close all info windows
			this.markers.forEach(m => {
				if (m.infoWindow) m.infoWindow.close();
			});

			this.editingGeofenceCompId = compId;

			// Store the building location for validation
			this.editingBuildingLocation = compData.position;

			// Zoom to the building location
			if (this.map && compData.position) {
				this.map.setCenter(compData.position);
				this.map.setZoom(17); // Close zoom level for geofence editing
			}

			// If geofence exists, make it editable
			if (this.geofenceCircles[compId]) {
				this.currentEditingCircle = this.geofenceCircles[compId];
				this.currentEditingCircle.setEditable(true);
				this.currentEditingCircle.setDraggable(true);
				this.currentEditingCircle.setOptions({
					fillOpacity: 0.4,
					strokeWeight: 3
				});

				// Listen for circle edits with debouncing
				google.maps.event.addListener(this.currentEditingCircle, 'radius_changed', () => {
					this.debouncedSaveGeofence();
				});
				google.maps.event.addListener(this.currentEditingCircle, 'center_changed', () => {
					this.debouncedSaveGeofence();
				});
			} else {
				// No geofence exists, create default 100m circular geofence
				this.createDefaultCircularGeofence(compId, compData.position);
			}

			// Show notification
			if (this.$notify) {
				this.$notify({
					type: 'success',
					title: 'Editing Mode',
					text: this.geofenceCircles[compId]
						? 'Drag the circle or resize using the edge handles. Saves immediately.'
						: 'Default 100m circular geofence created. Drag or resize to adjust. Saves immediately.'
				});
			}
		},

		createDefaultCircularGeofence(compId, center) {
			// Create a circular geofence with default radius
			const circle = new google.maps.Circle({
				center: center,
				radius: this.defaultGeofenceRadius,
				fillColor: '#FF6B6B',
				fillOpacity: 0.4,
				strokeWeight: 3,
				strokeColor: '#FF0000',
				editable: true,
				draggable: true,
				map: this.map
			});

			// Store the circle
			this.currentEditingCircle = circle;
			this.geofenceCircles[compId] = circle;

			// Listen for circle edits with debouncing
			google.maps.event.addListener(circle, 'radius_changed', () => {
				this.debouncedSaveGeofence();
			});
			google.maps.event.addListener(circle, 'center_changed', () => {
				this.debouncedSaveGeofence();
			});

			// Save the default geofence immediately (only once)
			this.saveGeofenceImmediately();
		},

		debouncedSaveGeofence() {
			// Clear existing timeout
			if (this.saveGeofenceTimeout) {
				clearTimeout(this.saveGeofenceTimeout);
			}

			// Set new timeout - save after 1 second of no changes
			this.saveGeofenceTimeout = setTimeout(() => {
				this.saveGeofenceImmediately();
			}, 1000);
		},

		handleCircleComplete(circle) {
			if (!this.editingGeofenceCompId) {
				// Not in editing mode, remove the circle
				circle.setMap(null);
				return;
			}

			// Validate that building location is inside the circle
			if (!this.validateBuildingInsideCircle(circle)) {
				circle.setMap(null);
				if (this.$notify) {
					this.$notify({
						type: 'error',
						title: 'Error',
						text: 'The property location must be inside the geofence circle. Please redraw.'
					});
				}
				// Re-enable drawing mode
				this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE);
				return;
			}

			// Store the new circle
			this.currentEditingCircle = circle;
			this.geofenceCircles[this.editingGeofenceCompId] = circle;

			// Disable drawing mode
			this.drawingManager.setDrawingMode(null);

			// Make it editable
			circle.setEditable(true);
			circle.setDraggable(true);

			// Listen for circle edits - save immediately
			google.maps.event.addListener(circle, 'radius_changed', () => {
				this.saveGeofenceImmediately();
			});
			google.maps.event.addListener(circle, 'center_changed', () => {
				this.saveGeofenceImmediately();
			});

			// Save immediately when circle is completed
			this.saveGeofenceImmediately();
		},

		validateBuildingInsideCircle(circle) {
			if (!this.editingBuildingLocation) return true;

			// Check if building location is within circle radius
			const buildingLatLng = new google.maps.LatLng(
				this.editingBuildingLocation.lat,
				this.editingBuildingLocation.lng
			);

			const distance = google.maps.geometry.spherical.computeDistanceBetween(
				buildingLatLng,
				circle.getCenter()
			);

			return distance <= circle.getRadius();
		},

		async saveGeofenceImmediately() {
			if (!this.editingGeofenceCompId || !this.currentEditingCircle) {
				return;
			}

			// Prevent recursive saves
			if (this.isSavingGeofence) {
				console.log('Already saving geofence, skipping...');
				return;
			}

			// Validate building is still inside circle
			if (!this.validateBuildingInsideCircle(this.currentEditingCircle)) {
				if (this.$notify) {
					this.$notify({
						type: 'error',
						title: 'Error',
						text: 'The property location must be inside the geofence. Please adjust the circle.'
					});
				}
				return;
			}

			// Set saving flag
			this.isSavingGeofence = true;

			// Get circle data
			const center = this.currentEditingCircle.getCenter();
			const radius = this.currentEditingCircle.getRadius();

			// Convert circle to polygon coordinates (32 points for smooth circle)
			const numPoints = 32;
			const coordinates = [];
			for (let i = 0; i < numPoints; i++) {
				const angle = (i * 360 / numPoints);
				const point = google.maps.geometry.spherical.computeOffset(
					center,
					radius,
					angle
				);
				coordinates.push({
					lat: point.lat(),
					lng: point.lng()
				});
			}

			try {
				// editingGeofenceCompId is the tour_comp id
				const tourCompId = this.editingGeofenceCompId;

				// Find the comp name for the notification
				const event = this.events.find(e => e.id === tourCompId);
				let compName = 'Property';
				if (event && event.compId) {
					const comp = this.availableComps.find(c => (c.pivot?.id || c.id) === event.compId);
					if (comp) {
						compName = this.getCompName(comp);
					}
				}

				// Save to backend using polygon coordinates format
				await axios.put(
					`/tours/${this.tour.id}/comps/${tourCompId}/geofence`,
					{
						coordinates: coordinates,
						name: `${compName} - Tour Geofence`,
						trigger_on_enter: true,
						trigger_on_exit: true
					}
				);

				console.log(`Geofence saved successfully (${Math.round(radius)}m radius)`);

				// Show success notification
				if (this.$notify) {
					this.$notify({
						type: 'success',
						title: 'Geofence Saved',
						text: `${Math.round(radius)}m radius geofence saved`
					});
				}

			} catch (error) {
				console.error('Error saving geofence:', error);
				if (this.$notify) {
					this.$notify({
						type: 'error',
						title: 'Error',
						text: error.response?.data?.message || 'Failed to save geofence'
					});
				}
			} finally {
				// Clear saving flag after a short delay to prevent rapid re-triggers
				setTimeout(() => {
					this.isSavingGeofence = false;
				}, 500);
			}
		},

		cancelGeofenceEdit() {
			if (!this.editingGeofenceCompId) return;

			// If we were drawing a new circle, remove it
			if (this.currentEditingCircle && !this.geofenceCircles[this.editingGeofenceCompId]) {
				this.currentEditingCircle.setMap(null);
			}

			// If we were editing an existing circle, restore it
			if (this.geofenceCircles[this.editingGeofenceCompId]) {
				this.geofenceCircles[this.editingGeofenceCompId].setEditable(false);
				this.geofenceCircles[this.editingGeofenceCompId].setDraggable(false);
				this.geofenceCircles[this.editingGeofenceCompId].setOptions({
					fillOpacity: 0.2,
					strokeWeight: 2
				});
			}

			// Disable drawing mode
			this.drawingManager.setDrawingMode(null);

			// Clear editing state
			this.editingGeofenceCompId = null;
			this.currentEditingCircle = null;
			this.editingBuildingLocation = null;

			if (this.$notify) {
				this.$notify({
					type: 'success',
					title: 'Done',
					text: 'Geofence editing mode closed'
				});
			}
		}
	}
};
</script>
